import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { listAlerts } from 'features/alerts/api/alertsApi';
import { createCredential, getGithubIntegration, listCredentials } from 'features/agents/api/agentsApi';
import PropertyMultiselectInput from 'shared/components/filters/PropertyMultiselectInput';
import {
  EmptyHint, Field, Notice, SecretInput, SelectField, TextInput, cx, ghostBtnCls, neutralBtnCls, useIssue,
} from 'features/agents/WorkflowEditor/fields/inputs';

/** Any of the team's alerts: the agent runs when any of them fires. */
export function AlertMultiPicker({ field, value, onChange }) {
  const { team } = useParams();
  const [rows, setRows] = useState(null);
  const issue = useIssue(field.key);
  useEffect(() => {
    let cancelled = false;
    listAlerts()
      .then((r) => { if (!cancelled) setRows(Array.isArray(r) ? r : (r?.alerts || [])); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (rows === null) return <Field label={field.label}><EmptyHint>Loading</EmptyHint></Field>;
  if (!rows.length) {
    return (
      <Field label={field.label} fieldKey={field.key}>
        <EmptyHint>
          <span>No alerts yet. Create one in <a className="underline" href={`/${encodeURIComponent(team || '')}/alerts`}>Alerts</a>.</span>
        </EmptyHint>
      </Field>
    );
  }
  const selected = (value || []).map(String);
  // An alert deleted since it was picked still shows, so it can be removed.
  const known = new Set(rows.map((a) => String(a.id)));
  const options = [
    ...rows.map((a) => ({ value: String(a.id), label: a.enabled === false ? `${a.name} (paused)` : a.name })),
    ...selected.filter((id) => !known.has(id)).map((id) => ({ value: id, label: 'Deleted alert' })),
  ];
  return (
    <Field
      label={field.label}
      fieldKey={field.key}
      help="Runs when any of these fires. The alert and its value arrive as the trigger input."
      action={selected.length ? <span className="text-[11px] text-[color:var(--text-muted)]">{selected.length} selected</span> : null}
    >
      <div className={cx(issue && 'rounded-md ring-1 ring-[color:var(--feedback-error)]')}>
        <PropertyMultiselectInput
          value={selected}
          onChange={(next) => onChange((next || []).map(String))}
          options={options}
          placeholder="Choose alerts"
        />
      </div>
    </Field>
  );
}

/** A saved credential, or for GitHub the team's App install. */
export function CredentialPicker({ field, value, onChange }) {
  const { team } = useParams();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const isGithub = (field.kindFilter || []).includes('oauth_github');
  const [githubApp, setGithubApp] = useState(null); // the team's GitHub App install, if any
  const [adding, setAdding] = useState(false);
  const issue = useIssue(field.key);

  const refresh = useCallback(async () => {
    try {
      const rows = await listCredentials();
      const kinds = field.kindFilter || [];
      setCreds(kinds.length ? rows.filter((r) => kinds.includes(r.kind)) : rows);
    } catch (_) { /* leave the list as it was */ } finally { setLoading(false); }
  }, [field.kindFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // GitHub steps can use the team's GitHub App: a short-lived token is minted
  // per call, so nothing is pasted here.
  useEffect(() => {
    if (!isGithub) return undefined;
    let cancelled = false;
    getGithubIntegration()
      .then((i) => { if (!cancelled) setGithubApp(i && i.installation_id ? i : false); })
      .catch(() => { if (!cancelled) setGithubApp(false); });
    return () => { cancelled = true; };
  }, [isGithub]);

  const options = [
    ...(isGithub && githubApp ? [{ value: 'github_app', label: `Team GitHub App${githubApp.github_login ? ` · ${githubApp.github_login}` : ''}` }] : []),
    ...creds.map((c) => ({ value: c.id, label: c.display_name })),
  ];
  const noApp = isGithub && githubApp === false && !value;

  return (
    <Field
      label={field.label}
      fieldKey={field.key}
      help={noApp ? (
        <>Install the GitHub App in <a className="underline" href={`/${encodeURIComponent(team || '')}/settings/integrations?integration=github`}>Integrations</a>, or add a token.</>
      ) : null}
    >
      <SelectField
        value={value || ''}
        onChange={(v) => onChange(v || null)}
        options={options}
        invalid={!!issue}
        placeholder={loading ? 'Loading' : options.length ? 'Select credential' : 'No credential yet'}
      />
      {isGithub ? (
        adding ? (
          <GithubTokenForm
            onCancel={() => setAdding(false)}
            onSaved={async (id) => { setAdding(false); await refresh(); onChange(id); }}
          />
        ) : (
          <button type="button" onClick={() => setAdding(true)} className={cx(ghostBtnCls, 'mt-1 -ml-1.5')}>Add a token instead</button>
        )
      ) : null}
    </Field>
  );
}

/** A GitHub personal access token, saved as a team credential and tested. */
function GithubTokenForm({ onCancel, onSaved }) {
  const [name, setName] = useState('GitHub token');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await createCredential({ kind: 'oauth_github', display_name: name.trim() || 'GitHub token', payload: { token: token.trim() } });
      if (out?.test && out.test.ok === false) setError(`Saved, but GitHub said: ${out.test.message || 'the token did not work'}`);
      onSaved(out?.credential?.id);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not save the token.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 flex flex-col gap-1.5 rounded-md border border-[color:var(--border-default)] p-2">
      <TextInput value={name} onChange={setName} placeholder="Name" drop={false} />
      <SecretInput autoComplete="off" value={token} onChange={setToken} placeholder="ghp_… or github_pat_…" />
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onCancel} className={ghostBtnCls}>Cancel</button>
        <button type="button" onClick={save} disabled={busy || !token.trim()} className={cx(neutralBtnCls, 'h-7')}>{busy ? 'Saving' : 'Save token'}</button>
      </div>
    </div>
  );
}
